// frontend/src/components/UsageDisplay.tsx
//
// Displays current credit balance and storage usage.

import { Link } from 'react-router-dom';
import { useUsage, formatBytes } from '../hooks/useUsage';
import { Coins, HardDrive, Loader2, AlertTriangle, Zap, ShoppingCart } from 'lucide-react';
import { getTierDisplayName } from '../lib/supabase';
import { PAYMENTS_DISABLED } from '../lib/payments';

interface UsageDisplayProps {
  compact?: boolean;
  showUpgradeLink?: boolean;
}

export function UsageDisplay({ compact = false, showUpgradeLink = true }: UsageDisplayProps) {
  const { data, loading, error } = useUsage();

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-theme-text-muted">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading usage...</span>
      </div>
    );
  }

  if (error || !data) {
    return null;
  }

  const totalCredits = data.credits.totalRemaining;
  const subscriptionTotal = data.credits.subscriptionTotal;
  const isLowCredits = totalCredits < data.costs.sponsoredAiCall * 3; // less than 3 sponsored calls
  const isOutOfCredits = totalCredits < data.costs.builderAiPer30Sec; // can't even make one builder call

  if (compact) {
    return (
      <div className="flex items-center gap-4 text-sm">
        <div className="flex items-center gap-1.5">
          <Coins className={`h-4 w-4 ${isOutOfCredits ? 'text-theme-error' : isLowCredits ? 'text-amber-500' : 'text-theme-text-muted'}`} />
          <span className={isOutOfCredits ? 'text-theme-error' : 'text-theme-text-secondary'}>
            {totalCredits} credits
          </span>
        </div>
        {isOutOfCredits && showUpgradeLink && !PAYMENTS_DISABLED && (
          <Link to="/pricing" className="text-theme-primary hover:underline text-xs">
            Get Credits
          </Link>
        )}
      </div>
    );
  }

  return (
    <div className="card p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-theme-text">Usage</h3>
        <span className="text-xs text-theme-text-muted px-2 py-0.5 bg-theme-surface rounded-full">
          {getTierDisplayName(data.tier as any)}
        </span>
      </div>

      {isOutOfCredits && (
        <div className="flex items-center gap-2 p-3 bg-theme-error-bg rounded-lg">
          <AlertTriangle className="h-4 w-4 text-theme-error flex-shrink-0" />
          <div className="text-sm">
            <span className="text-theme-error font-medium">Out of credits. </span>
            {showUpgradeLink && !PAYMENTS_DISABLED && (
              <Link to="/pricing" className="text-theme-primary hover:underline">
                Get more credits
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Credit Balance */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-1.5">
            <Coins className="h-4 w-4 text-theme-text-muted" />
            <span className="text-theme-text-secondary">Subscription credits</span>
          </div>
          <span className={`font-medium ${isLowCredits ? 'text-amber-500' : 'text-theme-text'}`}>
            {data.credits.subscriptionRemaining} / {subscriptionTotal}
          </span>
        </div>
      </div>

      {/* PAYG Credits */}
      {data.credits.paygRemaining > 0 && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-1.5">
              <ShoppingCart className="h-4 w-4 text-theme-text-muted" />
              <span className="text-theme-text-secondary">Pay-as-you-go credits</span>
            </div>
            <span className="font-medium text-theme-text">
              {data.credits.paygRemaining}
            </span>
          </div>
          {data.credits.paygExpiresAt && (
            <p className="text-xs text-theme-text-muted">
              Expires {new Date(data.credits.paygExpiresAt).toLocaleDateString()}
            </p>
          )}
        </div>
      )}

      {/* Credit Costs Info */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-1.5">
            <Zap className="h-4 w-4 text-theme-text-muted" />
            <span className="text-theme-text-secondary">Sponsored AI call cost</span>
          </div>
          <span className="font-medium text-theme-text">
            {data.costs.sponsoredAiCall} credits
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-1.5">
            <Zap className="h-4 w-4 text-theme-text-muted" />
            <span className="text-theme-text-secondary">Builder AI (per 30s)</span>
          </div>
          <span className="font-medium text-theme-text">
            {data.costs.builderAiPer30Sec} credits
          </span>
        </div>
      </div>

      {/* Storage */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-1.5">
            <HardDrive className="h-4 w-4 text-theme-text-muted" />
            <span className="text-theme-text-secondary">Storage used</span>
          </div>
          <span className="font-medium text-theme-text">
            {formatBytes(data.storage.bytesUsed)} / {formatBytes(data.storage.bytesLimit)}
          </span>
        </div>
      </div>

      {showUpgradeLink && !PAYMENTS_DISABLED && !isOutOfCredits && data.tier !== 'premium' && (
        <div className="pt-2 border-t border-theme-border-muted">
          <Link 
            to="/pricing" 
            className="text-sm text-theme-primary hover:underline"
          >
            Upgrade for more credits →
          </Link>
        </div>
      )}
    </div>
  );
}
